import React, { useState, useEffect } from 'react';
import { Menu, MenuItem, Typography, Box, Button, Divider } from '@mui/material';
import { supabase } from '../supabaseClient';

/* ---------------------------------------------------------
   MASTER CATEGORY + DOMAIN MAPPING  (from Version A)
--------------------------------------------------------- */
const CATEGORY_MAP = {
  finance: {
    label: "Finance Agent",
    items: {
      "Finance Insight": "Cost Center",
      // Finance Reports emails a written report; Finance Analyst answers
      // in chat (with charts). Both read the same company statements.
      "Finance Reports": "Analytics",
      "Finance Analyst": "Finance Analyst",
      // AP vendor line items (ZVENDOR_OPEN_SRV). Open-item / ageing / payment
      // questions live here, not on the Vendor Invoice agent.
      "Vendor Ledger": "Vendor Ledger"
    }
  },

  purchase: {
    label: "Purchase Agent",
    items: {
      "Vendor": "Vendor",
      "Purchase Requisition": "Purchase Requisition",
      "Purchase Order": "Purchase Orders",
      "Goods Movement": "Goods Movement",
      "Vendor Invoice": "Vendor Invoice",
      // Not a chat agent: compares uploaded quotations (PurchaseOptimizer.js).
      "Purchase Optimizer": "Purchase Optimizer"
    }
  },

  hr: {
    label: "HR Agent",
    items: {
      "Employees": "Human Resource",
      "Policy": "Policy Repository"
    }
  },

  sales: {
    label: "Sales Agent",
    items: {
      "Customers": "Business Partner",
      "Sales Order": "Sales Order",
      "Sales Quotation": "Sales Quotation",
      "Opportunities": "NA"
    }
  },

  inventory: {
    label: "Inventory Agent",
    items: {
      "Inventory": "Inventory"
    }
  },

  palmtop: {
    label: "Demo agent",
    items: {
      "Palmtop": "Palmtop"
    }
  }
};

// Palmtop (Demo agent) is restricted to this single demo account.
const PALMTOP_DEMO_EMAIL = "palmtop_demo@gmail.com";

const DomainSelector = ({ value, onDomainChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [allowedAgents, setAllowedAgents] = useState([]);
  const open = Boolean(anchorEl);

  /* ---------------------------------------------------------
     1. LOAD ALLOWED AGENTS FROM SUPABASE
  --------------------------------------------------------- */
  useEffect(() => {
    const loadAccess = async () => {
      const { data: userData } = await supabase.auth.getUser();
      const email = userData?.user?.email;

      if (!email) return;

      const { data: profile } = await supabase
        .from("user_profiles")
        .select("agents")
        .eq("email", email)
        .single();

      // Extract agents where permission array length > 0
      const activeAgents = Object.entries(profile?.agents || {})
        .filter(([_, perms]) => perms?.length > 0)
        .map(([agent]) => agent);

      // Palmtop is only available to the demo account, regardless of Supabase grants.
      const isPalmtopDemoUser = email.toLowerCase() === PALMTOP_DEMO_EMAIL;
      const filteredAgents = activeAgents.filter(agent => agent !== "palmtop");
      if (isPalmtopDemoUser) {
        filteredAgents.push("palmtop");
      }
      const nextAgents = filteredAgents;

      setAllowedAgents(nextAgents);

      // Fix: if last selected agent is NOT allowed → force popup
      const domainIsValid = nextAgents.some(agentKey => {
        const category = CATEGORY_MAP[agentKey];
        if (!category) return false;

        return Object.values(category.items).includes(value);
      });

      if (!domainIsValid) {
        onDomainChange(""); // clear invalid domain
      }
    };

    loadAccess();
  }, [value, onDomainChange]);

  /* ---------------------------------------------------------
     2. HANDLE SELECT
  --------------------------------------------------------- */
  const handleSelect = (domainKey) => {
    if (domainKey !== "NA") {
      onDomainChange(domainKey);
      setAnchorEl(null);
    }
  };

  /* ---------------------------------------------------------
     3. GET LABEL TO SHOW ON BUTTON
  --------------------------------------------------------- */
  const getSelectedLabel = () => {
    for (const agentKey of Object.keys(CATEGORY_MAP)) {
      const category = CATEGORY_MAP[agentKey];
      for (const [label, key] of Object.entries(category.items)) {
        if (key === value) return label;
      }
    }
    return value || "Select Agent";
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {/* Brand */}
      <Typography variant="h5" sx={{ fontWeight: 900 }}>
        Infosight<span style={{ color: '#4D96FF' }}>AI</span>
      </Typography>

      <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

      <Typography variant="h6">Agent:</Typography>

      {/* Button */}
      <Button
        variant="outlined"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          borderRadius: "999px",
          textTransform: "none",
          fontWeight: 600,
          px: 2
        }}
      >
        {getSelectedLabel()}
      </Button>

      {/* Dropdown */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ sx: { minWidth: 240 } }}
      >
        {allowedAgents.length === 0 && (
          <MenuItem disabled>No Agents Available</MenuItem>
        )}

        {allowedAgents.map((agentKey, idx) => {
          const category = CATEGORY_MAP[agentKey];
          if (!category) return null;

          return (
            <Box key={agentKey}>
              {/* Category Title */}
              <Typography
                sx={{
                  pl: 2,
                  pt: 1,
                  pb: 0.5,
                  fontWeight: 700,
                  fontSize: "0.9rem",
                  color: "text.secondary"
                }}
              >
                {category.label}
              </Typography>

              {/* Items */}
              {Object.entries(category.items).map(([label, domainKey]) => (
                <MenuItem
                  key={label}
                  disabled={domainKey === "NA"}
                  onClick={() => handleSelect(domainKey)}
                >
                  {label}
                </MenuItem>
              ))}

              {idx < allowedAgents.length - 1 && <Divider sx={{ my: 1 }} />}
            </Box>
          );
        })}
      </Menu>
    </Box>
  );
};

export default DomainSelector;
